/**
 * De collectiepagina (sections/collection-catalog.liquid): stemmingschips, Formaat en
 * Sorteer, zonder te herladen.
 *
 * Alle producten van de pagina staan al in de HTML, in de platte lijst, met hun gegevens
 * op het <li>: data-moods (",opfrissen,"), data-mix, data-pack, data-price, data-date,
 * data-index (volgorde van de collectie) en data-available. Filteren en sorteren is
 * hier dus alleen herschikken en verbergen. De gekozen stand gaat met replaceState in de
 * URL (?mood=…&pack=…&sort=…), zodat terugkomen vanaf een PDP hem herstelt en een link
 * hem deelt.
 *
 * Met een stemming gekozen: bovenaan de producten met alleen die stemming, en in
 * "Ook in deze mixen" de mixen die hem bevatten. Uitverkocht zakt altijd naar achteren.
 */

/** Onder deze breedte zijn er het filtervel en de plakbalk; de CSS gebruikt dezelfde grens. */
const MOBILE_MAX_WIDTH = 749;

const SORTS = ['price-asc', 'price-desc', 'newest'];

/** In de vertaalde teksten staat dit getal waar het aantal hoort (zie de sectie). */
const COUNT_PLACEHOLDER = '77777';
const TOTAL_PLACEHOLDER = '88888';

class CollectionCatalog extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {Record<string, any>} */
  #config = {};

  /** @type {HTMLElement[]} */
  #items = [];

  #state = { mood: '', pack: '', sort: '' };

  #pageSize = 12;
  #limit = 12;

  /** @type {number | null} */
  #frame = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    try {
      this.#config = JSON.parse(this.querySelector('[data-catalog-config]')?.textContent ?? '{}');
    } catch (error) {
      this.#config = {};
    }

    this.#pageSize = Number(this.dataset.pageSize) || 12;
    this.#limit = this.#pageSize;
    this.#items = Array.from(this.querySelectorAll('[data-catalog-item]'));

    const params = new URLSearchParams(window.location.search);
    this.#state = {
      mood: this.#validMood(params.get('mood')),
      pack: this.#validPack(params.get('pack')),
      sort: SORTS.includes(params.get('sort') ?? '') ? String(params.get('sort')) : '',
    };

    // De chips staan hier, de rijen "Per moment" in het geurpaneel ernaast: één
    // luisteraar op het geheel vangt ze allebei.
    const scope = this.closest('.catalog-scope') ?? this;
    scope.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });

    const sheet = this.#sheet;
    sheet?.addEventListener(
      'click',
      (event) => {
        // Een klik op de achtergrond van een modale <dialog> komt binnen op de dialog zelf.
        if (event.target === sheet) sheet.close();
      },
      { signal }
    );

    window.addEventListener('scroll', this.#queueSticky, { passive: true, signal });
    window.addEventListener('resize', this.#queueSticky, { passive: true, signal });

    this.#render();
    this.removeAttribute('data-pending');
    this.#revealCurrentChip();
    this.#updateSticky();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  get #sheet() {
    const sheet = this.querySelector('[data-catalog-sheet]');
    return sheet instanceof HTMLDialogElement ? sheet : null;
  }

  /** @param {string | null} value */
  #validMood(value) {
    if (!value) return '';
    return Object.prototype.hasOwnProperty.call(this.#config.moods ?? {}, value) ? value : '';
  }

  /** @param {string | null} value */
  #validPack(value) {
    if (!value) return '';
    const option = Array.from(this.querySelectorAll('[data-catalog-pack]')).some(
      (input) => input instanceof HTMLSelectElement
        ? Array.from(input.options).some((item) => item.value === value)
        : input instanceof HTMLInputElement && input.value === value
    );
    return option ? value : '';
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const moodLink = target.closest('[data-catalog-mood]');
    if (moodLink instanceof HTMLElement) {
      // Nieuw tabblad of venster: laat de browser zijn gang gaan.
      if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)) return;
      event.preventDefault();

      const mood = this.#validMood(moodLink.dataset.catalogMood ?? '');
      const fromPanel = Boolean(moodLink.closest('[data-swatch-panel]'));
      if (fromPanel) {
        moodLink.closest('[data-swatch-panel]')?.hidePopover?.();
      }
      this.#set({ mood }, true);
      if (fromPanel) this.querySelector('[data-catalog-chips]')?.scrollIntoView({ block: 'start' });
      return;
    }

    if (!this.contains(target)) return;

    if (target.closest('[data-catalog-clear]')) {
      event.preventDefault();
      this.#set({ mood: '', pack: '', sort: '' }, true);
      return;
    }

    if (target.closest('[data-catalog-more-button]')) {
      this.#limit += this.#pageSize;
      this.#render();
      return;
    }

    if (target.closest('[data-catalog-sheet-open]')) {
      this.#sheet?.showModal();
      return;
    }

    if (target.closest('[data-catalog-sheet-close]')) {
      this.#sheet?.close();
      return;
    }

    if (target.closest('[data-catalog-to-chips]')) {
      this.querySelector('[data-catalog-chips]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement || target instanceof HTMLInputElement)) return;
    if (target.hasAttribute('data-catalog-pack')) this.#set({ pack: target.value }, false);
    if (target.hasAttribute('data-catalog-sort')) this.#set({ sort: target.value }, false);
  };

  /**
   * @param {Partial<{ mood: string, pack: string, sort: string }>} change
   * @param {boolean} fromChip
   */
  #set(change, fromChip) {
    const before = this.#state.mood;
    this.#state = { ...this.#state, ...change };
    this.#limit = this.#pageSize;
    this.#render();
    this.#writeUrl();
    this.#revealCurrentChip();

    if (fromChip && this.#state.mood !== before) {
      try {
        window.Shopify?.analytics?.publish('collection_mood_filter', { mood: this.#state.mood || 'alle' });
      } catch (error) {
        // tracking mag het filter nooit in de weg zitten
      }
    }
  }

  #render() {
    const { mood, pack, sort } = this.#state;
    const filtered = Boolean(mood || pack || sort);
    const flatView = this.dataset.hasGroups !== 'true' || filtered;

    const groups = this.querySelector('[data-catalog-groups]');
    const flat = this.querySelector('[data-catalog-flat]');
    if (groups instanceof HTMLElement) groups.hidden = flatView;
    if (flat instanceof HTMLElement) flat.hidden = !flatView;

    /** @type {HTMLElement[]} */
    const main = [];
    /** @type {HTMLElement[]} */
    const mixes = [];
    /** @type {HTMLElement[]} */
    const rest = [];

    for (const item of this.#items) {
      const moods = (item.dataset.moods ?? '').split(',').filter(Boolean);
      const isMix = item.dataset.mix === '1';

      if (pack && item.dataset.pack !== pack) {
        rest.push(item);
      } else if (!mood) {
        main.push(item);
      } else if (mood === 'mix') {
        (isMix ? main : rest).push(item);
      } else if (moods.length === 1 && moods[0] === mood) {
        main.push(item);
      } else if (moods.length > 1 && moods.includes(mood)) {
        mixes.push(item);
      } else {
        rest.push(item);
      }
    }

    main.sort(this.#compare);
    mixes.sort(this.#compare);

    const grid = this.querySelector('[data-catalog-grid]');
    const mixGrid = this.querySelector('[data-catalog-mix-grid]');

    main.forEach((item, index) => {
      item.hidden = index >= this.#limit;
      grid?.append(item);
    });
    mixes.forEach((item) => {
      item.hidden = false;
      mixGrid?.append(item);
    });
    rest.forEach((item) => {
      item.hidden = true;
      grid?.append(item);
    });

    const isMood = Boolean(mood && mood !== 'mix');
    const mixSection = this.querySelector('[data-catalog-mixes]');
    if (mixSection instanceof HTMLElement) mixSection.hidden = !(isMood && mixes.length > 0);

    const empty = this.querySelector('[data-catalog-empty]');
    if (empty instanceof HTMLElement) empty.hidden = main.length + mixes.length > 0;

    const more = this.querySelector('[data-catalog-more]');
    if (more instanceof HTMLElement) {
      more.hidden = main.length <= this.#limit;
      const shown = this.querySelector('[data-catalog-shown]');
      if (shown) {
        shown.textContent = String(this.#config.shown ?? '')
          .replace(COUNT_PLACEHOLDER, String(Math.min(this.#limit, main.length)))
          .replace(TOTAL_PLACEHOLDER, String(main.length));
      }
    }

    // Aantal: "28 producten", of met een stemming "2 losse geuren + 4 mixen".
    let countText = this.#plural('count', main.length);
    if (isMood) {
      countText = this.#plural('singles', main.length);
      if (mixes.length > 0) countText += ` + ${this.#plural('mixes', mixes.length)}`;
    }
    const count = this.querySelector('[data-catalog-count]');
    if (count) count.textContent = countText;

    const sheetCount = this.querySelector('[data-catalog-sheet-count]');
    if (sheetCount) sheetCount.textContent = this.#plural('show', main.length + mixes.length);

    const moodLine = this.querySelector('[data-catalog-mood-line]');
    if (moodLine instanceof HTMLElement) {
      const text = isMood ? String(this.#config.moods?.[mood]?.text ?? '') : '';
      moodLine.textContent = text;
      moodLine.hidden = !text;
    }

    const stickyLabel = this.querySelector('[data-catalog-sticky-label]');
    if (stickyLabel) stickyLabel.textContent = mood ? String(this.#config.moods?.[mood]?.title ?? '') : String(this.#config.all ?? '');

    this.querySelectorAll('[data-catalog-mood]').forEach((chip) => {
      if (!(chip instanceof HTMLElement)) return;
      if ((chip.dataset.catalogMood ?? '') === mood) {
        chip.setAttribute('aria-current', 'true');
      } else {
        chip.removeAttribute('aria-current');
      }
    });

    this.querySelectorAll('[data-catalog-pack], [data-catalog-sort]').forEach((input) => {
      const value = input.hasAttribute('data-catalog-pack') ? pack : sort;
      if (input instanceof HTMLSelectElement) input.value = value;
      if (input instanceof HTMLInputElement) input.checked = input.value === value;
    });
  }

  /**
   * Leverbaar eerst, dan de gekozen sortering, dan de volgorde van de collectie.
   *
   * @param {HTMLElement} a
   * @param {HTMLElement} b
   */
  #compare = (a, b) => {
    const available = Number(b.dataset.available) - Number(a.dataset.available);
    if (available !== 0) return available;

    const number = (/** @type {HTMLElement} */ item, /** @type {string} */ key) => Number(item.dataset[key]) || 0;
    let order = 0;
    if (this.#state.sort === 'price-asc') order = number(a, 'price') - number(b, 'price');
    if (this.#state.sort === 'price-desc') order = number(b, 'price') - number(a, 'price');
    if (this.#state.sort === 'newest') order = number(b, 'date') - number(a, 'date');
    return order || number(a, 'index') - number(b, 'index');
  };

  /**
   * @param {string} key
   * @param {number} count
   */
  #plural(key, count) {
    const forms = this.#config[key] ?? {};
    if (count === 1) return String(forms.one ?? '');
    return String(forms.other ?? '').replace(COUNT_PLACEHOLDER, String(count));
  }

  #writeUrl() {
    const url = new URL(window.location.href);
    for (const key of /** @type {const} */ (['mood', 'pack', 'sort'])) {
      const value = this.#state[key];
      if (value) {
        url.searchParams.set(key, value);
      } else {
        url.searchParams.delete(key);
      }
    }
    window.history.replaceState(window.history.state, '', url);
  }

  /**
   * De gekozen chip in de scrollrij in beeld halen, zonder de pagina zelf te
   * verschuiven (scrollIntoView zou ook verticaal kunnen scrollen).
   */
  #revealCurrentChip() {
    const row = this.querySelector('.catalog-chips__row');
    const chip = row?.querySelector('[aria-current="true"]');
    if (!(row instanceof HTMLElement) || !(chip instanceof HTMLElement)) return;
    if (row.scrollWidth <= row.clientWidth) return;

    const left = chip.offsetLeft - row.offsetLeft;
    const right = left + chip.offsetWidth;
    if (left < row.scrollLeft + 16) {
      row.scrollLeft = left - 16;
    } else if (right > row.scrollLeft + row.clientWidth - 32) {
      row.scrollLeft = right - row.clientWidth + 32;
    }
  }

  #queueSticky = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#updateSticky();
    });
  };

  /**
   * De plakbalk (telefoon): zodra de chips boven uit beeld zijn, en zolang de
   * producten nog in beeld zijn. Per frame herberekend, net als de plakbalk op de PDP.
   */
  #updateSticky() {
    const bar = this.querySelector('[data-catalog-sticky]');
    if (!(bar instanceof HTMLElement)) return;

    let show = false;
    if (window.innerWidth <= MOBILE_MAX_WIDTH) {
      const chips = this.querySelector('[data-catalog-chips]') ?? this.querySelector('.catalog-bar');
      const chipsBottom = chips?.getBoundingClientRect().bottom ?? 0;
      const end = this.getBoundingClientRect().bottom;
      show = chipsBottom < 0 && end > 120;
    }
    if (bar.hidden === !show) return;
    bar.hidden = !show;
  }
}

if (!customElements.get('collection-catalog')) {
  customElements.define('collection-catalog', CollectionCatalog);
}
